import { useEffect } from "react"
import { getSocket } from "../lib/socket"
import { hydrateCurrentCallFromServer } from "../lib/call-center/current-call-sync"
import { useAuthStore } from "../stores/auth-store"
import { useCallStore } from "../stores/call-store"
import { getActiveCall } from "../lib/twilio"

interface UserUpdatedPayload {
  _id: string
  agent_is_in_queue?: boolean
  agent_current_call?: string | null
  agent_can_receive_calls?: boolean
}

interface AgentCallStatusPayload {
  _id: string
  company: string
  agent_can_receive_calls: boolean
  agent_is_in_queue: boolean
  agent_last_call_ended: number
  agent_last_call_started: number
  agent_current_call: string | null
}

type UpdateUser = (updates: Partial<Pick<UserUpdatedPayload & AgentCallStatusPayload, "agent_is_in_queue" | "agent_current_call" | "agent_can_receive_calls">>) => void

export function handleUserUpdatedEvent(
  data: UserUpdatedPayload,
  currentUserId: string,
  updateUser: UpdateUser
): void {
  if (data._id !== currentUserId) return

  if (data.agent_is_in_queue !== undefined) {
    useCallStore.getState().setIsInQueue(data.agent_is_in_queue)
  }

  updateUser({
    agent_is_in_queue: data.agent_is_in_queue,
    agent_current_call: data.agent_current_call,
    agent_can_receive_calls: data.agent_can_receive_calls,
  })

  if (data.agent_current_call) {
    void hydrateCurrentCallFromServer("users.updated")
  }
}

export function handleAgentCallStatusChangedEvent(
  data: AgentCallStatusPayload,
  currentUserId: string,
  updateUser: UpdateUser
): void {
  if (data._id !== currentUserId) return

  updateUser({
    agent_current_call: data.agent_current_call,
    agent_is_in_queue: data.agent_is_in_queue,
    agent_can_receive_calls: data.agent_can_receive_calls,
  })

  const callState = useCallStore.getState()
  const activeCall = getActiveCall()
  const twilioStatus = activeCall?.status()
  const isStillLive =
    twilioStatus === "open" ||
    twilioStatus === "connecting" ||
    twilioStatus === "reconnecting"

  if (!data.agent_current_call) {
    if (isStillLive) {
      return
    }

    if (callState.incomingCall || callState.currentCall || callState.transferCall) {
      useCallStore.getState().resetCallState()
    }
    return
  }

  void hydrateCurrentCallFromServer("agent-call-status-changed", {
    hasLiveTwilioCall: isStillLive,
  })
}

/**
 * Hook to handle call center specific socket events.
 * Listens for USERS.UPDATED and CALL_CENTER.AGENT_CALL_STATUS_CHANGED.
 */
export function useCallCenterSocket() {
  const { user, updateUser } = useAuthStore()
  const { setIsInQueue } = useCallStore()

  // Initialize isInQueue from user data on mount
  useEffect(() => {
    if (user?.agent_is_in_queue !== undefined) {
      setIsInQueue(user.agent_is_in_queue)
    }
  }, [user?.agent_is_in_queue, setIsInQueue])

  useEffect(() => {
    const socket = getSocket()
    if (!socket || !user) return

    const onUserUpdated = (data: UserUpdatedPayload) => {
      handleUserUpdatedEvent(data, user._id, updateUser)
    }

    const onAgentCallStatusChanged = (data: AgentCallStatusPayload) => {
      handleAgentCallStatusChangedEvent(data, user._id, updateUser)
    }

    socket.on("USERS.UPDATED", onUserUpdated)
    socket.on("CALL_CENTER.AGENT_CALL_STATUS_CHANGED", onAgentCallStatusChanged)

    return () => {
      socket.off("USERS.UPDATED", onUserUpdated)
      socket.off("CALL_CENTER.AGENT_CALL_STATUS_CHANGED", onAgentCallStatusChanged)
    }
  }, [user, setIsInQueue, updateUser])
}
